/**
 * Helpers that turn `BaseProps` token strings (`size`, `radius`, `shadow`,
 * `color`, `gradient`, `surface`) into concrete CSS values or style entries
 * the components inline as last resort.
 *
 * 90% of components rely solely on `--rr-*` CSS variables and these helpers
 * only kick in when the consumer passes a dynamic value (raw hex, custom
 * gradient object, runtime size string, …).
 */

import type { CSSProperties } from 'react';
import type { RadiusToken, ShadowToken, PaletteName, SurfaceInput } from './types.js';
import { palette, deriveSurfaceColors } from '@repletoryreach/sdk-utils/color';
import { gradients } from '@repletoryreach/sdk-utils/gradients';

const RADIUS_TOKENS: ReadonlySet<RadiusToken> = new Set([
  'none', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl', 'full'
]);

const SHADOW_TOKENS: ReadonlySet<ShadowToken> = new Set([
  'none', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl', 'inner'
]);

const PALETTE_NAMES: ReadonlySet<PaletteName> = new Set([
  'slate', 'gray', 'zinc', 'neutral', 'stone',
  'red', 'orange', 'amber', 'yellow', 'lime',
  'green', 'emerald', 'teal', 'cyan', 'sky',
  'blue', 'indigo', 'violet', 'purple', 'fuchsia',
  'pink', 'rose',
  'primary', 'secondary', 'tertiary'
] as const);

export function resolveRadius(value: RadiusToken | number | undefined): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return `${value}px`;
  if (RADIUS_TOKENS.has(value as RadiusToken)) return `var(--rr-radius-${value})`;
  return value;
}

export function resolveShadow(value: ShadowToken | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  if (SHADOW_TOKENS.has(value as ShadowToken)) return `var(--rr-shadow-${value})`;
  return value;
}

/**
 * Resolve a color prop to a CSS color string. Accepts a palette name
 * (e.g. `'teal'` → `var(--rr-color-teal-500)`), a role keyword
 * (`'primary' | 'secondary' | 'tertiary'`), or a raw CSS color.
 */
export function resolveColor(
  value: PaletteName | string | undefined,
  shade: keyof typeof palette['teal'] = 500
): string | undefined {
  if (!value) return undefined;
  if (value === 'primary' || value === 'secondary' || value === 'tertiary') {
    return `var(--rr-color-${value})`;
  }
  if (PALETTE_NAMES.has(value as PaletteName)) {
    return `var(--rr-color-${value}-${shade})`;
  }
  return value;
}

/** Semantic status names, which are not palette families but are what callers reach for. */
const SEMANTIC_NAMES: ReadonlySet<string> = new Set(['success', 'warning', 'error', 'info', 'danger']);

/**
 * The instance colour variables a component's stylesheet paints from.
 *
 * `color` used to travel to the DOM as `data-rr-color` and stop there: no
 * stylesheet read that attribute, so `<Badge color="green">` and every
 * `StatusBadge` tone fell through to the primary colour and rendered the same
 * amber pill. Resolving the name into `--rr-instance-*` here is what makes the
 * prop mean something, for every component that reads those variables.
 *
 * `ghost` is the translucent ground the light variants sit on. It is mixed
 * from the resolved colour rather than taken from a fixed tint table, so it
 * follows the colour in both themes.
 */
export function resolveInstanceColors(value: string | undefined): Record<string, string> | undefined {
  if (!value) return undefined;
  const base = SEMANTIC_NAMES.has(value)
    ? `var(--rr-color-${value === 'danger' ? 'error' : value})`
    : resolveColor(value);
  if (!base) return undefined;
  return {
    '--rr-instance-bg': base,
    '--rr-instance-ghost': `color-mix(in srgb, ${base} 14%, transparent)`,
    '--rr-instance-border': `color-mix(in srgb, ${base} 45%, transparent)`,
    // Hover and press in the same colour, a shade darker. Without these a red
    // button fell back to the brand colour's hover and turned teal under the
    // pointer (found 2 October 2026 on the Erase button).
    '--rr-instance-hover': `color-mix(in srgb, ${base} 86%, black)`,
    '--rr-instance-active': `color-mix(in srgb, ${base} 76%, black)`
  };
}

export function resolveSpacing(
  value: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string | undefined
): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return `${value}px`;
  if (['xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'].includes(value)) {
    return `var(--rr-spacing-${value})`;
  }
  return value;
}

/**
 * Convert a `surface={…}` prop into instance-scoped CSS variables so a
 * component's child CSS can read `var(--rr-instance-bg)` etc.
 */
export function resolveSurfaceStyle(surface: SurfaceInput | undefined): CSSProperties {
  if (!surface) return {};
  const s = deriveSurfaceColors(surface);
  return {
    ['--rr-instance-bg' as string]: s.background,
    ['--rr-instance-text' as string]: s.text,
    ['--rr-instance-text-muted' as string]: s.textMuted,
    ['--rr-instance-text-subtle' as string]: s.textSubtle,
    ['--rr-instance-border' as string]: s.border,
    ['--rr-instance-hover' as string]: s.hover,
    ['--rr-instance-active' as string]: s.active,
    ['--rr-instance-ghost' as string]: s.ghost,
    ['--rr-instance-focus-ring' as string]: s.focusRing,
    ['--rr-instance-secondary' as string]: s.secondary,
    ['--rr-instance-tertiary' as string]: s.tertiary
  };
}

/** Resolve a `gradient` prop to a CSS background string. */
export function resolveGradient(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const g = (gradients as Record<string, { kind: string; angle?: number; stops: Array<{ color: string; position?: number }> }>)[value];
  if (!g) return value; // assume raw CSS string
  const stopsCss = g.stops.map((s) => (s.position !== undefined ? `${s.color} ${s.position}%` : s.color)).join(', ');
  if (g.kind === 'radial') return `radial-gradient(circle, ${stopsCss})`;
  if (g.kind === 'conic') return `conic-gradient(from ${g.angle ?? 0}deg, ${stopsCss})`;
  return `linear-gradient(${g.angle ?? 135}deg, ${stopsCss})`;
}
